import type { DagDashboardResponse, DagListRunStatus } from '@/entities/dag';

import {
  DAG_FAVORITE_FILTER_VALUES,
  DAG_MATURITY_FILTER_VALUES,
  DAG_RUN_STATUS_FILTER_VALUES,
  type DagDashboardSortField,
  type DagDashboardSortOrder,
  type DagListFilterState,
  type DagTableRow,
} from './types';

type SummaryStatusCounts = Record<DagListRunStatus, number>;

const createEmptyStatusCounts = (): SummaryStatusCounts => ({
  draft: 0,
  failure: 0,
  success: 0,
  running: 0,
  pending: 0,
});

export const getSelectedDagVersion = (
  dag: DagDashboardResponse,
  selectedVersionByDagUuid: Record<string, number | undefined>
): DagTableRow['selectedVersion'] => {
  return selectedVersionByDagUuid[dag.dagUuid] ?? dag.deployedVersion ?? dag.latestVersion ?? 'initialized';
};

export const buildDagTableRows = (
  items: DagDashboardResponse[],
  selectedVersionByDagUuid: Record<string, number | undefined>
): DagTableRow[] => {
  return items.map((item) => {
    const selectedVersion = getSelectedDagVersion(item, selectedVersionByDagUuid);
    const selectedVersionItem = item.versions?.find((version) => version.version === selectedVersion) ?? null;
    const isSelectedDeployedVersion =
      item.deployedVersion !== null &&
      item.deployedVersion !== undefined &&
      selectedVersion !== null &&
      selectedVersion !== undefined &&
      item.deployedVersion === selectedVersion;

    return {
      ...item,
      selectedVersion,
      selectedVersionItem,
      displaySchedule: isSelectedDeployedVersion ? item.schedule : null,
      displayNextRun: isSelectedDeployedVersion ? item.nextRun : null,
      displayStatus: selectedVersionItem?.status ?? undefined,
      displayLastRun: selectedVersionItem?.lastRun ?? null,
      versionSnapshots: (item.versions ?? []).map((version) => ({
        versionLabel: version.version.toString(),
        runStatus: version.status ?? undefined,
        lastRunAt: version.lastRun ?? undefined,
      })),
    };
  });
};

const includesText = (source: string | undefined | null, keyword: string) => {
  return source?.toLowerCase().includes(keyword) ?? false;
};

export const filterDagTableRows = (rows: DagTableRow[], filters: DagListFilterState): DagTableRow[] => {
  const normalizedKeyword = filters.searchQuery.trim().toLowerCase();
  const activeStatuses = filters.selectedRunStatus.filter((status) => status !== DAG_RUN_STATUS_FILTER_VALUES.ALL);
  const activeFavorites = filters.selectedFavorite.filter((value) => value !== DAG_FAVORITE_FILTER_VALUES.ALL);
  const activeMaturities = filters.selectedMaturity.filter((value) => value !== DAG_MATURITY_FILTER_VALUES.ALL);
  const activeTags = new Set(filters.selectedTags);

  return rows.filter((row) => {
    if (
      normalizedKeyword &&
      !includesText(row.name, normalizedKeyword) &&
      !includesText(row.description, normalizedKeyword) &&
      !includesText(row.dagUuid, normalizedKeyword)
    ) {
      return false;
    }

    if (activeStatuses.length > 0 && (!row.displayStatus || !activeStatuses.includes(row.displayStatus))) {
      return false;
    }

    if (activeFavorites.length === 1) {
      if (activeFavorites[0] === DAG_FAVORITE_FILTER_VALUES.FAVORITES && !row.isBookmarked) {
        return false;
      }

      if (activeFavorites[0] === DAG_FAVORITE_FILTER_VALUES.NON_FAVORITES && row.isBookmarked) {
        return false;
      }
    }

    if (activeMaturities.length > 0 && !activeMaturities.includes(row.maturity)) {
      return false;
    }

    if (activeTags.size > 0) {
      const tags = row.tags ?? [];

      if (!tags.some((tag) => activeTags.has(tag))) {
        return false;
      }
    }

    return true;
  });
};

const compareNullableDate = (left?: string | null, right?: string | null) => {
  const leftTime = left ? new Date(left).getTime() : Number.NEGATIVE_INFINITY;
  const rightTime = right ? new Date(right).getTime() : Number.NEGATIVE_INFINITY;

  return leftTime - rightTime;
};

export const sortDagTableRows = (
  rows: DagTableRow[],
  sortBy?: DagDashboardSortField,
  sortOrder?: DagDashboardSortOrder
): DagTableRow[] => {
  if (!sortBy || !sortOrder) {
    return rows;
  }

  const direction = sortOrder === 'asc' ? 1 : -1;
  const sortedRows = [...rows];

  sortedRows.sort((left, right) => {
    if (sortBy === 'name') {
      return left.name.localeCompare(right.name) * direction;
    }

    if (sortBy === 'next_run') {
      return compareNullableDate(left.displayNextRun, right.displayNextRun) * direction;
    }

    return compareNullableDate(left.displayLastRun, right.displayLastRun) * direction;
  });

  return sortedRows;
};

export const paginateDagTableRows = (rows: DagTableRow[], currentPage: number, pageSize: number): DagTableRow[] => {
  const offset = (currentPage - 1) * pageSize;
  return rows.slice(offset, offset + pageSize);
};

export const countDagSummaryStatuses = (rows: DagTableRow[]): SummaryStatusCounts => {
  return rows.reduce<SummaryStatusCounts>((counts, row) => {
    const status = row.displayStatus ?? DAG_RUN_STATUS_FILTER_VALUES.DRAFT;
    counts[status] += 1;
    return counts;
  }, createEmptyStatusCounts());
};
